import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, Input, Modal, Radio, Select, Typography } from "antd";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { createNote, updateNote } from "@store/actions/notes.actions";
import type { Note, NotesState } from "@store/reducers/notes.reducer";
import { formattedPlayerName } from "@utils/name_utils";
import { readNoteApiError } from "@utils/noteApiError.mjs";
import {
    LIMITS,
    bodyLength,
    buildAudienceOptions,
    buildNotePayload,
    columnTagsWarning,
    initialNoteForm,
    missingColumnTags,
    restoreColumnTags,
    validateNoteForm,
    withSuggestedTags,
} from "@utils/notesForm.mjs";
import type { NoteFormState } from "@utils/notesForm.mjs";
import { NoteAttributesField } from "./NoteAttributesField";
import { NoteTagsField } from "./NoteTagsField";

export interface NoteDialogProps {
    open: boolean;
    /** Set: edit this note. Not set: create a note. */
    note?: Note;
    /** The player select shows this player and is disabled. */
    lockedPlayerId?: number;
    /** The tags of a new note. */
    initialTags?: string[];
    /** The tags of a Visualizer column. The dialog warns when the user removes one of them. */
    columnTags?: string[];
    /** Replaces the default title. */
    title?: string;
    onClose: () => void;
}

/** The props that change from one opening of the dialog to the next. */
export type NoteDialogRequest = Pick<NoteDialogProps, "note" | "lockedPlayerId" | "initialTags" | "columnTags" | "title">;

interface TeamPlayer {
    player_id: number;
    first_name: string;
    last_name: string;
}

type ServerErrors = Partial<Record<string, string>>;

// The input of each field of the request. An error of the server that names one of them shows next to it.
const SERVER_FIELDS = ["body", "player_id", "tags", "attributes", "visibility", "audience"];

const NO_ERRORS = { player: null, body: null, attributes: [], audience: null, hasErrors: false };

const VISIBILITY_OPTIONS = [
    { label: "Open", value: "open" },
    { label: "Targeted", value: "targeted" },
];

const FieldError = ({ text }: { text?: string | null }) =>
    text ? (
        <Typography.Text type="danger" className="notes-dialog-error">
            {text}
        </Typography.Text>
    ) : null;

type NoteDialogBodyProps = Omit<NoteDialogProps, "open" | "title">;

const NoteDialogBody = ({ note, lockedPlayerId, initialTags, columnTags, onClose }: NoteDialogBodyProps) => {
    const dispatch = useDispatch<any>();
    const players: TeamPlayer[] = useSelector(({ team }: any) => team.players);
    const teamName: string = useSelector(({ team }: any) => team.info.display_name);
    const coachList = useSelector(({ team }: any) => team.info.coach_list);
    const myUuid: string | undefined = useSelector(({ auth }: any) => auth.currentUser.uuid);
    const teamTags = useSelector((state: { notes: NotesState }) => state.notes.tags);
    const attributes = useSelector((state: { notes: NotesState }) => state.notes.attributes);

    const [form, setForm] = useState<NoteFormState>(() => initialNoteForm({ note, lockedPlayerId, initialTags }));
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tagError, setTagError] = useState<string | null>(null);
    const [serverErrors, setServerErrors] = useState<ServerErrors>({});

    const errors = useMemo(() => validateNoteForm(form, attributes), [form, attributes]);
    // A missing text or value shows after the first Save. From then on, every message follows the form.
    const shown = submitted ? errors : NO_ERRORS;

    const playerOptions = useMemo(() => {
        const options = players.map((p) => ({ value: p.player_id, label: formattedPlayerName(teamName, `${p.first_name} ${p.last_name}`) }));
        // A retired player is not in the team list, but the note keeps the player.
        if (note?.player && !options.some((o) => o.value === note.player?.id)) {
            options.push({ value: note.player.id, label: formattedPlayerName(teamName, note.player.name) });
        }
        return options;
    }, [players, teamName, note]);
    const audienceOptions = useMemo(() => buildAudienceOptions(coachList, myUuid, note), [coachList, myUuid, note]);

    const missingTags = columnTags ? missingColumnTags(form.tags, columnTags) : [];
    const bodyCount = bodyLength(form.body);

    const change = (patch: Partial<NoteFormState>, ...fields: string[]) => {
        setForm((current) => ({ ...current, ...patch }));
        setServerErrors((current) => Object.fromEntries(Object.entries(current).filter(([field]) => !fields.includes(field))));
    };

    const handleTagsChange = (tags: string[], error: string | null) => {
        setTagError(error);
        change({ tags }, "tags");
    };

    const handleRestoreTags = () => {
        const restored = restoreColumnTags(form.tags, columnTags ?? []);
        handleTagsChange(restored.tags, restored.error);
    };

    const handleSave = async () => {
        setSubmitted(true);
        if (errors.hasErrors) return;
        setSaving(true);
        setServerErrors({});
        const payload = buildNotePayload(form);
        try {
            await dispatch(note ? updateNote(note.uuid, payload) : createNote(payload));
            onClose();
        } catch (e) {
            // The thunk shows an error that has no field. An error with a field shows next to its input.
            const { message: text, field } = readNoteApiError(e);
            if (field === null) return;
            if (SERVER_FIELDS.includes(field)) setServerErrors({ [field]: text });
            else message.error(text);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="notes-dialog">
            <div className="notes-dialog-fields">
                <div className="notes-dialog-field">
                    <label className="notes-dialog-label" htmlFor="notes-dialog-player">
                        Player
                    </label>
                    <div className="notes-dialog-help">Who this note is about. Players can't see notes.</div>
                    <Select
                        id="notes-dialog-player"
                        className="notes-full-width"
                        showSearch
                        optionFilterProp="label"
                        disabled={saving || lockedPlayerId !== undefined}
                        placeholder="Choose a player"
                        options={playerOptions}
                        value={lockedPlayerId ?? form.playerId ?? undefined}
                        onChange={(value: number) => change({ playerId: value }, "player_id")}
                    />
                    <FieldError text={shown.player ?? serverErrors.player_id} />
                </div>

                <div className="notes-dialog-field">
                    <div className="notes-dialog-label-row">
                        <label className="notes-dialog-label" htmlFor="notes-dialog-body">
                            Note
                        </label>
                        <Typography.Text type={bodyCount > LIMITS.body ? "danger" : "secondary"} className="notes-dialog-count">
                            {bodyCount.toLocaleString()} / {LIMITS.body.toLocaleString()}
                        </Typography.Text>
                    </div>
                    <Input.TextArea
                        id="notes-dialog-body"
                        rows={4}
                        disabled={saving}
                        placeholder="Write the note…"
                        value={form.body}
                        onChange={(e) => change({ body: e.target.value }, "body")}
                    />
                    <FieldError text={shown.body ?? serverErrors.body} />
                </div>

                <div className="notes-dialog-field">
                    <label className="notes-dialog-label" htmlFor="notes-dialog-tags">
                        Tags <span className="notes-dialog-optional">optional · up to {LIMITS.tags}</span>
                    </label>
                    <div className="notes-dialog-help">{'Short labels that group notes, such as "Injury" or "Fall 2026". You can filter notes by tags.'}</div>
                    {missingTags.length > 0 && (
                        <Alert
                            className="notes-dialog-warning"
                            type="warning"
                            message={columnTagsWarning(missingTags)}
                            action={
                                <Button type="link" size="small" onClick={handleRestoreTags} data-attr="notes.note-dialog.add-tags-back">
                                    {missingTags.length === 1 ? "Add it back" : "Add them back"}
                                </Button>
                            }
                        />
                    )}
                    <NoteTagsField tags={form.tags} knownTags={withSuggestedTags(teamTags.map((t) => t.name))} disabled={saving} onChange={handleTagsChange} />
                    <FieldError text={tagError ?? serverErrors.tags} />
                </div>

                <div className="notes-dialog-field">
                    <span className="notes-dialog-label">
                        Attributes <span className="notes-dialog-optional">optional · up to {LIMITS.attributes}</span>
                    </span>
                    <div className="notes-dialog-help">{'Numerical values to track alongside notes, such as "Score" or "Grade" (1–5).'}</div>
                    <NoteAttributesField
                        rows={form.attributeRows}
                        attributes={attributes}
                        errors={shown.attributes}
                        disabled={saving}
                        onChange={(attributeRows) => change({ attributeRows }, "attributes")}
                    />
                    <FieldError text={serverErrors.attributes} />
                </div>

                <div className="notes-dialog-field">
                    <span className="notes-dialog-label">Visibility</span>
                    <div className="notes-dialog-help">Who on the staff can read this note.</div>
                    <Radio.Group
                        optionType="button"
                        disabled={saving}
                        options={VISIBILITY_OPTIONS}
                        value={form.visibility}
                        onChange={(e) => change({ visibility: e.target.value }, "visibility", "audience")}
                    />
                    <FieldError text={serverErrors.visibility} />
                    {form.visibility === "open" ? (
                        <div className="notes-dialog-field-foot">
                            <Typography.Text type="secondary" className="notes-dialog-hint">
                                Everyone on the team who can view notes can see this note.
                            </Typography.Text>
                        </div>
                    ) : (
                        <div className="notes-dialog-audience">
                            <Select
                                id="notes-dialog-audience"
                                className="notes-full-width"
                                mode="multiple"
                                showSearch
                                optionFilterProp="label"
                                disabled={saving}
                                placeholder="Add a person…"
                                options={audienceOptions}
                                value={form.audience}
                                onChange={(audience: string[]) => change({ audience }, "audience")}
                            />
                            <div className="notes-dialog-field-foot">
                                <Typography.Text type="secondary" className="notes-dialog-hint">
                                    You can always see your own notes.
                                </Typography.Text>
                                <Typography.Text type="secondary" className="notes-dialog-count">
                                    {form.audience.length} / {LIMITS.audience}
                                </Typography.Text>
                            </div>
                            <FieldError text={shown.audience ?? serverErrors.audience} />
                        </div>
                    )}
                </div>
            </div>

            <div className="notes-dialog-footer">
                <Button disabled={saving} onClick={onClose} data-attr="notes.note-dialog.cancel">
                    Cancel
                </Button>
                <Button
                    type="primary"
                    loading={saving}
                    disabled={submitted && errors.hasErrors}
                    onClick={handleSave}
                    data-attr="notes.note-dialog.save"
                >
                    {note ? "Save changes" : "Save note"}
                </Button>
            </div>
        </div>
    );
};

export const NoteDialog = ({ open, title, ...bodyProps }: NoteDialogProps) => (
    // The body mounts when the dialog opens and unmounts when it closes, so each opening starts from a fresh form.
    <Modal
        open={open}
        title={title ?? (bodyProps.note ? "Edit note" : "New note")}
        width={620}
        footer={null}
        maskClosable={false}
        destroyOnHidden
        onCancel={bodyProps.onClose}
    >
        <NoteDialogBody {...bodyProps} />
    </Modal>
);
